import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import VenueBottomNav from '../../components/VenueBottomNav';
import { RouteTransitionLogo } from '../../components/RouteTransitionLogo';
import { feedbackPayArrived } from '../../utils/appFeedback';
import { useAutoCancelDraft } from '../../hooks/useAutoCancelDraft';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatEuro = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('en-GB', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  });
};

export default function VenueAwaitingPayment() {
  const navigate = useNavigate();
  const { txnId } = useParams();
  const [txn, setTxn] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Auto-cancel the draft if the staff navigates away / closes the app
  // without resolving the payment. Prevents a stale PENDING_GUEST from
  // resurfacing for the guest later.
  useAutoCancelDraft({ txnId, status: txn?.status, kind: 'real' });

  // Fetch initial transaction details so we can show the bill summary,
  // then poll every 2s for status changes.
  useEffect(() => {
    if (!txnId) return undefined;
    let cancelled = false;
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    const fetchTxn = async () => {
      try {
        const res = await axios.get(`${API}/transactions/${txnId}`, { headers });
        if (cancelled) return;
        setTxn(res.data);
        if (res.data.status === 'PAID') {
          // Descending arrival chime + "arrived" vibration — pairs with the
          // guest phone's rising send tone so the two devices feel connected.
          feedbackPayArrived();
          navigate(`/venue/transaction/${txnId}`, { replace: true });
        } else if (res.data.status === 'CANCELLED' || res.data.status === 'EXPIRED') {
          // Silent recovery — no toast. The staff just goes back to the scan
          // page as if nothing happened; a 60s wait with no guest action is a
          // routine timeout, not an error worth alerting on.
          navigate('/venue/scan', { replace: true });
        }
      } catch (err) {
        if (cancelled) return;
        setLoadError(err.response?.data?.detail || 'Could not load transaction');
      }
    };

    fetchTxn();
    const interval = setInterval(fetchTxn, 2000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [txnId, navigate]);

  const handleCancel = useCallback(async () => {
    if (!txnId) return;
    setCancelling(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/transactions/${txnId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Transaction cancelled');
      navigate('/venue/scan', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not cancel transaction');
      setCancelling(false);
      setConfirmingCancel(false);
    }
  }, [txnId, navigate]);

  return (
    <div className="app-shell" data-testid="venue-awaiting-page" style={{
      minHeight: '100vh', backgroundColor: 'var(--app-bg)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'flex-start', paddingBottom: 140, paddingTop: 32,
      paddingLeft: 24, paddingRight: 24,
    }}>
      {/* Logo */}
      <div style={{ marginTop: 16 }}>
        <RouteTransitionLogo width={110} height={98} dataTestId="venue-awaiting-payment-shared-logo" />
      </div>

      {/* Spinning green loader */}
      <div style={{ marginTop: 18, position: 'relative', width: 50, height: 50 }}>
        <svg width="45" height="45" viewBox="0 0 45 45" fill="none" xmlns="http://www.w3.org/2000/svg"
          style={{ animation: 'spin 1.2s linear infinite' }}>
          <mask id="path-1-inside-1_await" fill="white">
            <path d="M22.5 45C34.9264 45 45 34.9264 45 22.5C45 10.0736 34.9264 0 22.5 0C10.0736 0 0 10.0736 0 22.5C0 34.9264 10.0736 45 22.5 45Z"/>
          </mask>
          <path d="M53 22.5C47.6667 22.5 42.3333 22.5 37 22.5C37.0835 24.0383 36.9293 25.5723 36.5333 27.0617C35.0207 33.0736 29.1688 38.0114 22.5 38.25C14.1516 38.9132 5.72331 31.5833 5.5 22.5C4.82219 13.4845 12.7495 4.45873 22.5 4.25C30.1914 3.78647 38.066 8.89688 40.8133 16.5484C41.5149 18.4378 41.9172 20.4481 42 22.5C44 22.5 46 22.5 48 22.5C48.0845 19.8324 47.7511 17.1344 46.9955 14.5409C44.0934 4.07336 33.81 -4.06723 22.5 -4.25C8.2807 -5.04155 -5.40501 7.54576 -5.5 22.5C-6.30614 37.3864 6.87861 51.6696 22.5 51.75C34.8326 52.2694 47.1386 43.9561 51.2755 31.8489C52.3367 28.8555 52.9182 25.6811 53 22.5Z" fill="#1F8E2A" mask="url(#path-1-inside-1_await)"/>
        </svg>
      </div>

      {/* Caption */}
      <h2 data-testid="awaiting-headline" style={{
        fontFamily: "'Inria Serif', serif", fontSize: 22, fontWeight: 700,
        color: 'var(--text)', textAlign: 'center', marginTop: 14, marginBottom: 0,
      }}>
        Awaiting payment…
      </h2>

      {/* Bill summary */}
      {loadError && (
        <p style={{ marginTop: 24, fontSize: 14, color: 'var(--status-danger-text)', textAlign: 'center' }}>
          {loadError}
        </p>
      )}

      {txn && (
        <div data-testid="awaiting-bill-summary" style={{
          marginTop: 28,
          width: '100%', maxWidth: 340,
          background: 'var(--surface)',
          border: '1px solid var(--card-border)',
          borderRadius: 18,
          padding: '18px 20px',
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          <SummaryRow label="Bill" value={formatEuro(txn.original_amount)} />
          {txn.discount_percent > 0 && (
            <SummaryRow
              label={`Allure discount · ${Math.round(txn.discount_percent)}%`}
              value={`− ${formatEuro(txn.original_amount - txn.discounted_amount)}`}
              valueColor="var(--status-success-text)"
            />
          )}
          <div style={{ height: 1, background: 'var(--hairline)', marginTop: 4, marginBottom: 4 }} />
          <SummaryRow
            label="Customer pays"
            value={formatEuro(txn.discounted_amount)}
            bold
            big
          />
          {txn.customer_id && (
            <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 6, marginBottom: 0, textAlign: 'right' }}>
              Customer ID · <span style={{ fontFamily: 'monospace', letterSpacing: 1 }}>{txn.customer_id}</span>
            </p>
          )}
        </div>
      )}

      {/* Cancel — only visible while truly pending */}
      {txn && txn.status === 'PENDING_GUEST' && (
        <div style={{ marginTop: 24, width: '100%', maxWidth: 340 }}>
          {!confirmingCancel ? (
            <button
              type="button"
              data-testid="awaiting-cancel-btn"
              onClick={() => setConfirmingCancel(true)}
              style={{
                width: '100%', height: 48,
                background: 'transparent',
                color: 'var(--status-danger-text)',
                border: '1px solid var(--status-danger-surface)',
                borderRadius: 999,
                fontSize: 15, fontWeight: 500,
                cursor: 'pointer',
                transition: 'background-color 160ms ease',
              }}
            >
              Cancel transaction
            </button>
          ) : (
            <div style={{
              border: '1px solid var(--status-danger-surface)',
              background: 'var(--status-danger-surface)',
              borderRadius: 18,
              padding: 16,
              display: 'flex', flexDirection: 'column', gap: 12,
            }}>
              <p style={{ margin: 0, fontSize: 14, color: 'var(--status-danger-text)', textAlign: 'center' }}>
                Cancel this transaction? The customer can&rsquo;t pay it after you confirm.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  data-testid="awaiting-cancel-back-btn"
                  onClick={() => setConfirmingCancel(false)}
                  disabled={cancelling}
                  style={{
                    flex: 1, height: 44,
                    background: 'var(--surface)',
                    color: 'var(--text)',
                    border: '1px solid var(--card-border)',
                    borderRadius: 999,
                    fontSize: 14, fontWeight: 500,
                    cursor: cancelling ? 'not-allowed' : 'pointer',
                  }}
                >
                  Keep waiting
                </button>
                <button
                  type="button"
                  data-testid="awaiting-cancel-confirm-btn"
                  onClick={handleCancel}
                  disabled={cancelling}
                  style={{
                    flex: 1, height: 44,
                    background: '#C62828',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: 999,
                    fontSize: 14, fontWeight: 600,
                    cursor: cancelling ? 'not-allowed' : 'pointer',
                    opacity: cancelling ? 0.7 : 1,
                  }}
                >
                  {cancelling ? 'Cancelling…' : 'Yes, cancel'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* CSS animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      <VenueBottomNav />
    </div>
  );
}

function SummaryRow({ label, value, valueColor, bold, big }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <span style={{
        fontSize: big ? 15 : 14,
        color: 'var(--text-2)',
        fontWeight: bold ? 600 : 400,
      }}>{label}</span>
      <span style={{
        fontFamily: "'Inria Serif', serif",
        fontSize: big ? 22 : 16,
        fontWeight: bold ? 700 : 500,
        color: valueColor || 'var(--text)',
      }}>{value}</span>
    </div>
  );
}
